import { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../../../components/custom-table/custom-table";
import DocViewerWithSideNavigation from "../../../../../components/modals/docViewerWithSideNavigation/docViewerWithSideNavigation";
import ImageViewerWithBadgesModal from "../../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import {
  FilterSection,
  FilterValues,
} from "../../../../../data-model/filterValues";
import { ModalId } from "../../../../../enums/modalId";
import { useExportTableData } from "../../../../../hooks/useExportTableData";
import { setFilter } from "../../../../../redux/slice/filterValuesSlice";
import { closeModal as closeReduxModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import {
  handleDocUploadScreenshotViewForExternalOrders,
  handlePropagateEventOnClickInRpaStatusCell,
} from "../../../../../utils/medicalPaOrdersTableHandlers";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import {
  getFilterCreationConfig,
  useFilterOptionsGeneratorFromAlgolia,
} from "../../../hooks";
import { applySunstateUrologyFilterOverrides } from "../../../medicalPaOrdersAlgolia/utils/applySunstateUrologyFilterOverrides";
import { useExternalFilterAndSearch } from "../hooks/useExternalFilterAndSearch";
import { useExternalNextPageDataFetch } from "../hooks/useExternalNextPageDataFetch";
import { useExternalTableData } from "../hooks/useExternalTableData";
import { useInitialDataFetchForExternalTable } from "../hooks/useInitialDataFetchForExternalTable";
import { AllPaOrdersForExternalTableByAlgoliaHeaders } from "../utils/allPaOrdersForExternalByAlgoliaHelper";

/**
 * Table component for displaying all external PA orders with server-backed search.
 * Delegates pagination, filtering, search, data export, and refresh to dedicated hooks,
 * following the same architecture as medicalPaOrdersTable.
 *
 * @param allPaOrdersForExternalTableRef - Ref attached to the table container div for layout/scroll behavior
 * @returns The custom table UI wired to external worklist hooks
 */
const AllPaOrdersForExternalTableByAlgolia = ({
  allPaOrdersForExternalTableRef,
}: {
  allPaOrdersForExternalTableRef: React.RefObject<HTMLDivElement>;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modal,
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const abortController = useRef<AbortController | null>(null);

  const { externalTableState, setExternalTableState, tableData, setTableData } =
    useExternalTableData();

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchForExternalTable(setExternalTableState, setTableData);

  const {
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    filterPayload,
    validFilters,
  } = useExternalFilterAndSearch(
    initialDataFetchingExecuted,
    setTableData,
    setExternalTableState,
  );

  const { setEndIndex } = useExternalNextPageDataFetch(
    validFilters,
    initialDataFetchingExecuted,
    searchTerm,
    externalTableState,
    setExternalTableState,
    setTableData,
  );

  const orgId = getOrgIdForFetchExternalWorklist();

  const { filterData, isPostgresFacetResultsLoading } =
    useFilterOptionsGeneratorFromAlgolia(getFilterCreationConfig(orgId));

  const filterSectionsData = useMemo(
    () => applySunstateUrologyFilterOverrides(filterData, orgId),
    [filterData, orgId],
  );

  const { exportData, loading: exportLoading } = useExportTableData();

  const handleRefreshButtonClick = () => setInitialDataFetchingExecuted(false);

  const handleNavigateToNextPage = (rowData: any) => {
    navigate(`/external-worklist/singleOrderForExternal/${rowData.id}`);
  };

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  return (
    <div className="h-full" ref={allPaOrdersForExternalTableRef}>
      <CustomTable
        tableHeaders={AllPaOrdersForExternalTableByAlgoliaHeaders}
        tableData={tableData}
        tableName={"allMedicalPaOrdersForExternalByAlgolia"}
        itemsPerPage={20}
        pagesPerView={5}
        count={tableData.length ?? 0}
        totalCount={externalTableState.data?.total_count ?? 0}
        endIndexOfTable={setEndIndex}
        searchingText={(searchText: string) => setSearchTerm(searchText)}
        hideSearchBar={false}
        onRowClick={(rowData: any) => handleNavigateToNextPage(rowData)}
        isFetching={externalTableState.loading}
        showExportButton={true}
        onExportButtonClick={() => {
          if (!filterPayload) return;
          exportData(
            { ...filterPayload, export: true },
            "external-pa-orders-export.csv",
          );
        }}
        showInLineLoader={externalTableState.showinLineLoader}
        showRefreshButton={true}
        onRefreshButton={handleRefreshButtonClick}
        isDisabled={externalTableState.loading}
        filterSectionsData={filterSectionsData as FilterSection[]}
        onFilterApplyClicked={handleFilterApply}
        requestForPaginationReset={paginationResetCount}
        exportingInProgress={exportLoading}
        showFilterButton={
          !externalTableState.loading && !isPostgresFacetResultsLoading
        }
        onRpaStatusForExternalClick={(modalType, orderId) => {
          if (modalType === "image") {
            handlePropagateEventOnClickInRpaStatusCell(
              orderId,
              dispatch,
              authStatusOptions,
              abortController,
            );
          } else if (modalType === "doc") {
            handleDocUploadScreenshotViewForExternalOrders(
              orderId,
              dispatch,
              abortController,
            );
          }
        }}
      />

      {openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION && (
        <DocViewerWithSideNavigation
          showLoader={metaData?.showLoader ?? false}
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
            dispatch(closeReduxModal());
          }}
        />
      )}
      {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
        <ImageViewerWithBadgesModal
          tabs={metaData?.tabs ?? []}
          showLoading={metaData?.showLoading ?? false}
          title="Write Back Trail"
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
            dispatch(closeReduxModal());
          }}
        />
      )}
    </div>
  );
};

export default AllPaOrdersForExternalTableByAlgolia;
